import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Printer, Trash2 } from 'lucide-react';
import { AccesRefuse, Button, Modal, PageHeader, Skeleton, StatusBadge } from '@/components/ui';
import type { StatusKey } from '@/types';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import {
  type Devis,
  type DevisInput,
  type DevisStatut,
  type LigneDevis,
  changerStatutDevis,
  createDevis,
  deleteDevis,
  listDevis,
  openDevisPdf,
  updateDevis,
} from '@/services/devis';
import { listTiers } from '@/services/tiers';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const STATUT_UI: Record<DevisStatut, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  ENVOYE: { label: 'Envoyé', status: 'working' },
  ACCEPTE: { label: 'Accepté', status: 'done' },
  REFUSE: { label: 'Refusé', status: 'stuck' },
  A_REVISER: { label: 'À réviser', status: 'working' },
  CONVERTI: { label: 'Converti (legacy)', status: 'info' },
};
// Statuts attribuables manuellement (cycle de vie). « Converti » exclu (mécanisme supprimé).
const STATUTS_CHOIX: { value: DevisStatut; label: string }[] = [
  { value: 'BROUILLON', label: 'Brouillon' },
  { value: 'ENVOYE', label: 'Envoyé' },
  { value: 'ACCEPTE', label: 'Accepté' },
  { value: 'A_REVISER', label: 'À réviser' },
  { value: 'REFUSE', label: 'Refusé' },
];
const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
type LigneForm = { designation: string; unite: string; quantite: number; prix_unitaire: number; remise_pct: number };
const ligneVide = (): LigneForm => ({ designation: '', unite: '', quantite: 0, prix_unitaire: 0, remise_pct: 0 });

export function DevisPage() {
  const perms = usePerms('achats');
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [clientId, setClientId] = useState('');
  const [objet, setObjet] = useState('');
  const [tvaPct, setTvaPct] = useState(0.19);
  const [exonere, setExonere] = useState(false);
  const [remiseGlobale, setRemiseGlobale] = useState(0); // en % (négociation)
  const [lignes, setLignes] = useState<LigneForm[]>([ligneVide()]);

  const { data, isLoading } = useQuery({ queryKey: ['devis'], queryFn: () => listDevis({ size: 100 }), enabled: perms.canConsulter });
  const { data: clients } = useQuery({ queryKey: ['devis-clients'], queryFn: () => listTiers({ type: 'CLIENT', size: 200 }), enabled: perms.canConsulter });
  const clientNom = useMemo(() => Object.fromEntries((clients?.items ?? []).map((c) => [c.id, c.raison_sociale])), [clients]);

  const reset = () => { setEditId(null); setClientId(''); setObjet(''); setTvaPct(0.19); setExonere(false); setRemiseGlobale(0); setLignes([ligneVide()]); };
  const invalidate = () => qc.invalidateQueries({ queryKey: ['devis'] });

  // Sous-total = Σ lignes (remises de ligne incluses) ; HT = sous-total − remise globale.
  const sousTotal = lignes.reduce((s, l) => s + (Number(l.quantite) || 0) * (Number(l.prix_unitaire) || 0) * (1 - (Number(l.remise_pct) || 0) / 100), 0);
  const totalHT = sousTotal * (1 - (Number(remiseGlobale) || 0) / 100);
  const totalTTC = totalHT * (exonere ? 1 : 1 + tvaPct);

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: DevisInput = {
        objet: objet || null, client_id: clientId || null, tva_pct: tvaPct, tva_exonere: exonere,
        remise_globale_pct: (Number(remiseGlobale) || 0) / 100,
        lignes: lignes.filter((l) => l.designation.trim()).map((l) => ({
          designation: l.designation, unite: l.unite || null, quantite: Number(l.quantite) || 0,
          prix_unitaire: Number(l.prix_unitaire) || 0, remise_pct: (Number(l.remise_pct) || 0) / 100,
        })),
      };
      return editId ? updateDevis(editId, payload) : createDevis(payload);
    },
    onSuccess: () => { invalidate(); setOpen(false); reset(); toast.success(editId ? 'Devis modifié.' : 'Devis créé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const statutMut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: DevisStatut }) => changerStatutDevis(id, statut),
    onSuccess: () => { invalidate(); toast.success('Statut mis à jour.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteDevis(id),
    onSuccess: () => { invalidate(); toast.success('Devis supprimé.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const ouvrirEdition = (d: Devis) => {
    setEditId(d.id); setClientId(d.client_id ?? ''); setObjet(d.objet ?? '');
    setTvaPct(Number(d.tva_pct)); setExonere(d.tva_exonere);
    setRemiseGlobale(Math.round((Number(d.remise_globale_pct) || 0) * 1000) / 10);
    setLignes(d.lignes.length ? d.lignes.map((l: LigneDevis) => ({
      designation: l.designation, unite: l.unite ?? '', quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire),
      remise_pct: Math.round((Number(l.remise_pct) || 0) * 1000) / 10,
    })) : [ligneVide()]);
    setOpen(true);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  if (!perms.canConsulter) {
    return <AccesRefuse detail="Les devis requièrent la permission « Achats / Factures ». Contactez votre administrateur." />;
  }

  const items = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Devis"
        subtitle="Propositions commerciales (BPU/métré) — négociation des prix. La facturation se fait au réel, séparément."
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Devis' }]}
        actions={perms.canModifier && <Button onClick={() => { reset(); setOpen(true); }}><Plus /> Nouveau devis</Button>}
      />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">
          Aucun devis. {perms.canModifier ? 'Créez-en un avec « Nouveau devis ».' : ''}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Référence</th>
                <th className="px-4 py-3 label-caps">Client</th>
                <th className="px-4 py-3 label-caps">Objet</th>
                <th className="px-4 py-3 text-right label-caps">Montant TTC</th>
                <th className="px-4 py-3 label-caps">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const st = STATUT_UI[d.statut];
                // Édition verrouillée une fois « accepté » (ou ancien « converti »). Le statut
                // reste modifiable manuellement (ex. repasser « à réviser » pour rouvrir).
                const verrou = d.statut === 'ACCEPTE' || d.statut === 'CONVERTI';
                return (
                  <tr key={d.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-semibold text-content-primary">{d.reference}</td>
                    <td className="px-4 py-2">{d.client_id ? clientNom[d.client_id] ?? '—' : '—'}</td>
                    <td className="px-4 py-2 text-content-secondary">{d.objet ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">{formatDZD(Number(d.montant_ttc))}</td>
                    <td className="px-4 py-2"><StatusBadge status={st.status} label={st.label} /></td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Imprimer le devis"
                          onClick={() => openDevisPdf(d.id, d.reference).catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Imprimer"><Printer className="size-4" /></Button>
                        {perms.canModifier && (
                          <select
                            className="h-8 rounded border border-border bg-surface-secondary px-1 text-xs"
                            value={d.statut}
                            title="Changer le statut du devis"
                            onChange={(e) => statutMut.mutate({ id: d.id, statut: e.target.value as DevisStatut })}
                          >
                            {d.statut === 'CONVERTI' && <option value="CONVERTI">Converti (legacy)</option>}
                            {STATUTS_CHOIX.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        )}
                        {perms.canModifier && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={verrou}
                            title={verrou ? 'Devis accepté — passez-le « à réviser » pour modifier' : 'Modifier'}
                            onClick={() => ouvrirEdition(d)} aria-label="Modifier"><Pencil className="size-4" /></Button>
                        )}
                        {perms.canSupprimer && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck"
                            onClick={() => { if (window.confirm('Supprimer ce devis ?')) delMut.mutate(d.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title={editId ? 'Modifier le devis' : 'Nouveau devis'} size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending}>{editId ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Client">
              <select className={selectCls} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">— Aucun —</option>
                {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.raison_sociale}</option>)}
              </select>
            </FormField>
            <FormField label="Objet"><Input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Réfection voirie…" /></FormField>
            <FormField label="TVA">
              <select className={selectCls} value={exonere ? 'EX' : String(tvaPct)} onChange={(e) => { if (e.target.value === 'EX') setExonere(true); else { setExonere(false); setTvaPct(Number(e.target.value)); } }}>
                <option value="0.19">19 %</option>
                <option value="0.09">9 %</option>
                <option value="EX">Exonéré</option>
              </select>
            </FormField>
          </div>

          <div className="rounded-lg border border-border">
            <div className="grid grid-cols-12 gap-1 border-b border-border bg-surface-primary px-2 py-1.5 label-caps">
              <div className="col-span-4">Désignation</div><div className="col-span-2">Unité</div>
              <div className="col-span-2 text-right">Qté</div><div className="col-span-2 text-right">P.U.</div>
              <div className="col-span-1 text-right">Rem.%</div><div className="col-span-1" />
            </div>
            {lignes.map((l, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-1 border-b border-border px-2 py-1 last:border-0">
                <Input className="col-span-4 h-8" value={l.designation} onChange={(e) => setLignes((m) => m.map((x, j) => j === i ? { ...x, designation: e.target.value } : x))} />
                <Input className="col-span-2 h-8" value={l.unite} onChange={(e) => setLignes((m) => m.map((x, j) => j === i ? { ...x, unite: e.target.value } : x))} />
                <Input type="number" step="1" className="col-span-2 h-8 text-right tabular-nums" value={l.quantite} onChange={(e) => setLignes((m) => m.map((x, j) => j === i ? { ...x, quantite: Number(e.target.value) } : x))} />
                <Input type="number" step="1" className="col-span-2 h-8 text-right tabular-nums" value={l.prix_unitaire} onChange={(e) => setLignes((m) => m.map((x, j) => j === i ? { ...x, prix_unitaire: Number(e.target.value) } : x))} />
                <Input type="number" step="1" className="col-span-1 h-8 text-right tabular-nums" value={l.remise_pct} onChange={(e) => setLignes((m) => m.map((x, j) => j === i ? { ...x, remise_pct: Number(e.target.value) } : x))} />
                <button className="col-span-1 text-status-stuck" onClick={() => setLignes((m) => m.length > 1 ? m.filter((_, j) => j !== i) : m)} aria-label="Retirer la ligne"><Trash2 className="size-4" /></button>
              </div>
            ))}
            <button className="px-2 py-1.5 text-sm font-medium text-brand-primary" onClick={() => setLignes((m) => [...m, ligneVide()])}>+ Ligne</button>
          </div>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <FormField label="Remise globale (%)" className="mb-0 w-40" hint="Rabais de négociation sur le sous-total">
              <Input type="number" step="1" value={remiseGlobale} onChange={(e) => setRemiseGlobale(Number(e.target.value))} />
            </FormField>
            <div className="space-y-0.5 text-right text-sm">
              {(remiseGlobale > 0 || lignes.some((l) => Number(l.remise_pct) > 0)) && (
                <div className="text-content-secondary">Sous-total HT : <span className="tabular-nums">{formatDZD(sousTotal, { decimals: 0 })}</span></div>
              )}
              {remiseGlobale > 0 && (
                <div className="text-status-stuck">Remise globale {remiseGlobale} % : −<span className="tabular-nums">{formatDZD(sousTotal - totalHT, { decimals: 0 })}</span></div>
              )}
              <div className="text-content-secondary">Total HT : <span className="font-bold tabular-nums text-content-primary">{formatDZD(totalHT, { decimals: 0 })}</span></div>
              <div className="text-content-secondary">Total TTC : <span className="font-bold tabular-nums text-content-primary">{formatDZD(totalTTC, { decimals: 0 })}</span></div>
            </div>
          </div>
          <p className={cn('text-xs text-content-muted')}>Les montants sont recalculés par le serveur à l'enregistrement. La remise (ligne ou globale) est répercutée sur HT, TVA et TTC.</p>
        </div>
      </Modal>
    </>
  );
}
